/**
 * Row icons for the notification centre.
 *
 * Transcribed from the icon switch that lived inside `pages/Notifications.tsx`
 * so every existing row keeps the exact icon it has today. Returns the icon
 * component rather than an element, so callers control sizing.
 */
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  ClipboardCheck,
  FileText,
  Megaphone,
  MessageSquare,
  Package,
  Scale,
  Star,
  Truck,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { NotificationType } from '../../lib/api';

const TYPE_ICONS: Record<NotificationType, LucideIcon> = {
  po_created: Package,
  po_status_changed: Package,
  po_received: Package,
  po_delivery_approaching: Truck,
  po_delivery_overdue: Truck,
  ncr_created: AlertTriangle,
  ncr_status_changed: AlertTriangle,
  ncr_assigned: AlertTriangle,
  supplier_rating_changed: Star,
  supplier_qualification_invite: FileText,
  supplier_qualification_complete: FileText,
  supplier_query_created: MessageSquare,
  supplier_query_responded: MessageSquare,
  rfq_created: FileText,
  rfq_response_received: FileText,
  rfq_awarded: FileText,
  rfq_cancelled: FileText,
  rfq_revision_requested: MessageSquare,
  user_invitation_sent: Users,
  user_invitation_accepted: Users,
  user_mentioned: Users,
  document_uploaded: FileText,
  document_expiring: FileText,
  document_acceptance_required: FileText,
  questionnaire_assigned: FileText,
  onboarding_invitation_sent: UserPlus,
  onboarding_invitation_accepted: UserPlus,
  onboarding_submitted: UserPlus,
  onboarding_approved: UserPlus,
  onboarding_declined: UserPlus,
  onboarding_info_requested: UserPlus,
  calloff_sent: Package,
  calloff_confirmed: Package,
  calloff_dispatched: Package,
  calloff_delivery_lines_ready: Package,
  calloff_delivered: Package,
  calloff_cancelled: Package,
  calloff_edited: Package,
  delivery_appeal_submitted: Scale,
  delivery_appeal_responded: Scale,
  contract_expiring: CalendarClock,
  audit_action_due_soon: ClipboardCheck,
  audit_action_overdue: ClipboardCheck,
  system_announcement: Megaphone,
  weekly_report: Megaphone,
};

/** Icon for a notification type; the bell for anything unrecognised. */
export function getNotificationIcon(type: string): LucideIcon {
  const icon = (TYPE_ICONS as Record<string, LucideIcon | undefined>)[type];
  return icon ?? Bell;
}
